import {
  Form,
  type OperationType,
  Space,
  SubmitButton,
  theme,
} from '@/components';
import { ProjectFormSchema } from '@/features/project/projects/_models/project';
import { useForm } from '@/hooks';
import { sheetTypeEnum } from '@/utils/enum';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FC, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import type { z } from 'zod';
import { RsProjectFields } from './RsProjectFields';

type FormValues = z.infer<typeof ProjectFormSchema>;

type Props = {
  type: OperationType;
  isSubmitEnabledOnMount?: boolean;
  initialValues?: {
    sheetType?: FormValues['sheetType'];
    data?: Partial<FormValues['data']>;
  };
  onSubmit: (values: FormValues) => void;
  ignoredIdsForRelatedProject?: string[];
  openUpdateConfirmModal?: boolean;
};

export const ProjectForm: FC<Props> = ({
  type,
  isSubmitEnabledOnMount,
  ignoredIdsForRelatedProject,
  initialValues: _initialValues,
  onSubmit,
  openUpdateConfirmModal = false,
}) => {
  const { token: _token } = theme.useToken();
  const initialValues = useMemo(() => {
    const data = _initialValues?.data ?? {};
    return {
      sheetType: _initialValues?.sheetType ?? sheetTypeEnum.Values.RS,
      data: {
        skipAdministrativeReformTeamEvaluation: false,
        ...data,
        // TODO: Parsed by sheetType
      } as any,
    };
  }, [_initialValues]);

  const methods = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(
      ProjectFormSchema.superRefine((_v, _ctx) => {
        // TODO: Add validation logic if needed
      }),
    ),
  });
  const {
    handleSubmit,
    formState: { isSubmitting, isSubmitSuccessful, isDirty },
  } = methods;

  const shouldShowLeaveAlert = useMemo<boolean>(
    () =>
      (isDirty &&
        !isSubmitting &&
        (!openUpdateConfirmModal || !isSubmitSuccessful)) ||
      openUpdateConfirmModal,
    [isDirty, isSubmitting, openUpdateConfirmModal, isSubmitSuccessful],
  );

  return (
    <FormProvider {...methods}>
      <Form
        leavePageAlertCondition={{
          additionalAlertCondition: shouldShowLeaveAlert,
        }}
        layout="vertical"
        onFinish={handleSubmit(onSubmit)}
      >
        <RsProjectFields
          operation={type}
          ignoredIdsForRelatedProject={ignoredIdsForRelatedProject}
        />
        <Space style={{ marginTop: 32 }}>
          <SubmitButton
            loading={isSubmitting}
            disabled={!isSubmitEnabledOnMount && !isDirty}
          />
        </Space>
      </Form>
    </FormProvider>
  );
};
